import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import GoalsStatusComponent from 'webapp/components/goals/status/index';
import ToolTipComponent from 'webapp/components/tool-tip';

import type GoalLog from 'webapp/models-custom/goal-log';

interface Args {
	model: GoalLog;
	selectedId: string | null;
	edit?: (model: GoalLog) => void;
}

export default class GoalLogsTableRowComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/goal-logs') declare mmGoalLogsService: Service.ModelManager.GoalLogs;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked showAll = false;

	get model() {
		return this.args.model;
	}

	get message() {
		return this.model.sanitizedMessage || '';
	}

	get isActivityLogDisabled() {
		return !this.billingService.checkIfCan(BillingFeature.ACTIVITY_LOG);
	}

	get isRowSelected() {
		return this.args.selectedId === this.model.id;
	}

	get showAction() {
		return !!this.model.status && this.model.havePermissionToEdit;
	}

	get isMessageTruncated() {
		return this.message.length > 150;
	}

	get truncatedMessage() {
		return this.isMessageTruncated ? `${this.message.slice(0, 150)}...` : this.message;
	}

	@action
	editGoalLog() {
		if (this.args.edit) {
			this.args.edit(this.model);
		}
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		<tr
			class={{joinClasses
				(array "table-row" (if this.showAll "all-shown") (if this.isRowSelected "active"))
			}}
			data-id={{@model.id}}
		>
			<td>
				{{#if @model.status}}
					<GoalsStatusComponent @status={{@model.status}} />
				{{else}}
					Goal created
				{{/if}}
			</td>

			<td class="message">
				{{#if this.showAll}}
					{{{@model.sanitizedMessage}}}
				{{else}}
					{{{this.truncatedMessage}}}
				{{/if}}
				{{#if this.isMessageTruncated}}
					<div class="toggle">
						<a role="button" {{on "click" this.toggleShowAll}}>
							<CommonUiIconComponent
								@name="ChevronDown"
								@size="xs"
								@class="toggle-icon"
							/>
							Show
							{{if this.showAll "less" "more"}}
						</a>
					</div>
				{{/if}}
				{{#if @model.modified}}
					<div class="edit-note">
						Edited
						<span>
							<span>
								{{@model.modifiedTextRelative}}
								<ToolTipComponent @text={{@model.modifiedText}} />
							</span>
						</span>
						{{#if @model.modifiedByUser}}
							by
							<span>{{@model.modifiedByUser.name}}</span>
						{{/if}}
					</div>
				{{/if}}
				<div class="edit-note">
					Updated
					<span>
						<span>
							{{@model.createdTextRelative}}
							<ToolTipComponent @text={{@model.createdText}} />
						</span>
					</span>
					{{#if @model.createdByUser}}
						by
						<span>{{@model.createdByUser.name}}</span>
					{{/if}}
				</div>
			</td>

			<td>
				<div class="value-compare {{@model.changeIndicatorClass}}">
					<span class="value">{{@model.valueStr}}</span>
					<span class="indicator">
						{{@model.changeStr}}
					</span>
				</div>
			</td>

			{{#if @edit}}
				<td class="action">
					{{#if this.showAction}}
						<a role="button" class="edit-log" {{on "click" this.editGoalLog}}>
							<CommonUiIconComponent @name="Pencil" @size="sm" />
						</a>
					{{/if}}
				</td>
			{{/if}}
		</tr>
	</template>
}
